import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReportIssue from './ReportIssue';
import { getApiUrl, env } from '../config/env.js';

const tips = [
  {
    icon: 'fa-microphone',
    title: 'Real-time transcription',
    description: 'Speak naturally during the consultation; MediScan automatically transcribes your exchanges.'
  },
  {
    icon: 'fa-language',
    title: 'Multilingual translation',
    description: 'Translate your reports into English, Spanish, German, Italian, or Arabic from the editor.'
  },
  {
    icon: 'fa-pause-circle',
    title: 'Smart pause',
    description: 'Pause recording at any time without losing your progress.'
  },
  {
    icon: 'fa-file-pdf',
    title: 'Professional PDF export',
    description: 'Generate formatted PDF reports ready to print or share.'
  },
  {
    icon: 'fa-edit',
    title: 'Flexible editing',
    description: 'Edit and correct your reports before finalizing for optimal accuracy.'
  },
  {
    icon: 'fa-headphones',
    title: 'Audio replay',
    description: 'Replay the audio of past consultations at any time.'
  },
  {
    icon: 'fa-brain',
    title: 'Advanced medical AI',
    description: 'The AI automatically extracts key information and organizes it by section.'
  },
  {
    icon: 'fa-shield-alt',
    title: 'Secure data',
    description: 'Your data is encrypted and stored securely, in compliance with HIPAA standards.'
  }
];

const HomePage = ({ onStartConsultation, onViewHistory, onViewConsultation, onNewConsultationWithPatient, doctorInfo }) => {
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState('');
  const { logout, user, authFetch } = useAuth();

  // Auto-rotate tips every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % tips.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch patients — skip in demo mode (no DynamoDB backing)
  useEffect(() => {
    if (env.IS_DEMO) {
      setLoading(false);
      return;
    }
    if (user?.id) {
      fetchPatients();
    } else {
      setLoading(false);
    }
  }, [user?.id, nameFilter]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (nameFilter.trim()) {
        params.append('name', nameFilter.trim());
      }

      const response = await authFetch(getApiUrl(`/api/consultations/list?${params.toString()}`));
      const data = await response.json();
      console.log('Patients API response:', data);

      if (data.success) {
        const allConsultations = data.consultations || [];

        // Group consultations by patient
        const patientMap = new Map();
        allConsultations.forEach(consultation => {
          const patientKey = `${consultation.patient_first_name}_${consultation.patient_last_name}_${consultation.patient_dob || ''}`;
          if (!patientMap.has(patientKey)) {
            patientMap.set(patientKey, {
              id: patientKey,
              firstName: consultation.patient_first_name,
              lastName: consultation.patient_last_name,
              fullName: consultation.patient_name,
              dob: consultation.patient_dob,
              sex: consultation.patient_sex,
              address: consultation.patient_address,
              consultations: [],
              lastConsultation: consultation.created_at
            });
          }
          patientMap.get(patientKey).consultations.push(consultation);
          if (new Date(consultation.created_at) > new Date(patientMap.get(patientKey).lastConsultation)) {
            patientMap.get(patientKey).lastConsultation = consultation.created_at;
          }
        });

        const patientList = Array.from(patientMap.values()).sort(
          (a, b) => new Date(b.lastConsultation) - new Date(a.lastConsultation)
        );
        setPatients(patientList);
      }
    } catch (err) {
      console.error('Error fetching patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateAge = (dateString) => {
    if (!dateString) return null;
    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleNewConsultation = (patient) => {
    // Parse fullName if firstName/lastName are undefined
    let firstName = patient.firstName;
    let lastName = patient.lastName;
    if ((!firstName || !lastName) && patient.fullName) {
      const nameParts = patient.fullName.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        firstName = firstName || nameParts[0];
        lastName = lastName || nameParts.slice(1).join(' ');
      } else if (nameParts.length === 1) {
        firstName = firstName || nameParts[0];
        lastName = lastName || '';
      }
    }
    if (onNewConsultationWithPatient) {
      onNewConsultationWithPatient({
        nom: lastName || '',
        prenom: firstName || '',
        dateNaissance: patient.dob || '',
        sexe: patient.sex || '',
        adresse: patient.address || ''
      });
    }
  };

  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: '#fafbfc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
    }}>
      {/* Top Navigation Bar */}
      <nav style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg style={{ width: '32px', height: '32px', color: '#059669' }} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L21.5 7v10L12 22 2.5 17V7L12 2z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M9 9h6M12 6v12M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>MediScan</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {doctorInfo?.name && (
            <>
              <div style={{ textAlign: 'right', marginRight: '8px' }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                  Dr. {doctorInfo.name}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {doctorInfo.specialty}
                </div>
              </div>
              <button
                onClick={logout}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f9fafb';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                <i className="fas fa-sign-out-alt" style={{ fontSize: '12px' }}></i>
                Sign out
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div style={{
        padding: '32px',
        paddingBottom: '120px',
        width: '100%'
      }}>
        {/* Welcome Header */}
        <div style={{
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '600',
              color: '#111827',
              margin: '0 0 8px 0'
            }}>
              Hello, Dr. {doctorInfo?.name || 'Physician'}
            </h1>
            <p style={{
              fontSize: '15px',
              color: '#6b7280',
              margin: 0,
              textTransform: 'capitalize'
            }}>
              {currentDate} • {currentTime}
            </p>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            fontSize: '13px',
            color: '#6b7280'
          }}>
            <span><strong style={{ color: '#111827' }}>12</strong> consultations</span>
            <span style={{ color: '#d1d5db' }}>•</span>
            <span><strong style={{ color: '#111827' }}>8</strong> min moy.</span>
            <span style={{ color: '#d1d5db' }}>•</span>
            <span><strong style={{ color: '#111827' }}>45</strong> rapports</span>
          </div>
        </div>

        {/* Quick Actions - Single Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: env.IS_DEMO ? '1fr' : '1fr 1fr',
          gap: '20px',
          marginBottom: '32px',
          ...(env.IS_DEMO ? { maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' } : {}),
        }}>
          <button
            onClick={onStartConsultation}
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              border: 'none',
              borderRadius: '12px',
              padding: '24px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'left',
              boxShadow: '0 2px 8px rgba(5, 150, 105, 0.15)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(5, 150, 105, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.15)';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className="fas fa-microphone" style={{ fontSize: '20px' }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 4px 0'
              }}>
                MediScan
              </h3>
              <p style={{
                fontSize: '13px',
                margin: 0,
                opacity: 0.9
              }}>
                Start a new consultation
              </p>
            </div>
          </button>

        </div>

        {/* Patients Section */}
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-users" style={{ color: '#059669', fontSize: '18px' }}></i>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: 0
              }}>
                {selectedPatient ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => setSelectedPatient(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#6b7280'
                      }}
                    >
                      <i className="fas fa-arrow-left" style={{ fontSize: '14px' }}></i>
                    </button>
                    Consultations for {selectedPatient.fullName}
                  </span>
                ) : 'My patients'}
              </h2>
            </div>

            {!selectedPatient && (
              <>
                {/* Quick Patient Search */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  flex: 1,
                  maxWidth: '360px',
                  marginLeft: 'auto',
                  marginRight: '16px',
                  transition: 'all 0.2s'
                }}>
                  <i className="fas fa-search" style={{ color: '#9ca3af', fontSize: '15px' }}></i>
                  <input
                    type="text"
                    placeholder="Search for a patient..."
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      fontSize: '15px',
                      color: '#374151',
                      width: '100%'
                    }}
                    onFocus={(e) => {
                      e.target.parentElement.style.borderColor = '#059669';
                      e.target.parentElement.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.parentElement.style.borderColor = '#e5e7eb';
                      e.target.parentElement.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {!env.IS_DEMO && (
                  <button
                    onClick={onViewHistory}
                    style={{
                      padding: '8px 14px',
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#6b7280',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'white';
                    }}
                  >
                    View all
                    <i className="fas fa-arrow-right" style={{ fontSize: '11px' }}></i>
                  </button>
                )}
              </>
            )}
          </div>

          {/* Patient List or Consultations */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '12px' }}></i>
              <p>Loading...</p>
            </div>
          ) : selectedPatient ? (
            // Show consultations for selected patient
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[...selectedPatient.consultations]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((consultation) => (
                  <div
                    key={consultation.id}
                    onClick={() => onViewConsultation && onViewConsultation(consultation)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: '#f9fafb',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: '#ecfdf5',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className="fas fa-file-medical" style={{ color: '#059669', fontSize: '16px' }}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {formatShortDate(consultation.created_at)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        {consultation.motif || 'Consultation'}
                      </div>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                ))}
            </div>
          ) : patients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <i className="fas fa-user-plus" style={{ fontSize: '32px', marginBottom: '12px', color: '#d1d5db' }}></i>
              <p style={{ margin: 0 }}>No patients found</p>
              <p style={{ fontSize: '13px', marginTop: '4px' }}>Start a new consultation to add a patient</p>
            </div>
          ) : (
            // Show patient list
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {patients.map((patient) => (
                <div
                  key={patient.id}
                  onClick={() => setSelectedPatient(patient)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '1px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '44px',
                    height: '44px',
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>
                      {(patient.firstName?.charAt(0) || patient.fullName?.charAt(0) || '').toUpperCase()}
                      {(patient.lastName?.charAt(0) || '').toUpperCase()}
                    </span>
                  </div>

                  {/* Patient Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h3 style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {patient.fullName}
                      </h3>
                      {calculateAge(patient.dob) !== null && (
                        <span style={{
                          fontSize: '13px',
                          color: '#6b7280',
                          fontWeight: '500'
                        }}>
                          {calculateAge(patient.dob)} yrs
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#6b7280' }}>
                      <span>{patient.consultations.length} consultation{patient.consultations.length !== 1 ? 's' : ''}</span>
                      <span style={{ color: '#d1d5db' }}>•</span>
                      <span>Last: {formatShortDate(patient.lastConsultation)}</span>
                    </div>
                  </div>

                  {/* New consultation button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNewConsultation(patient);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: '#059669',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      color: 'white',
                      fontSize: '20px',
                      fontWeight: '400',
                      marginRight: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#047857';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#059669';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="New consultation"
                  >
                    +
                  </button>

                  {/* Arrow */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Tips Carousel at Bottom */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        padding: '16px 32px',
        zIndex: 99,
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          {/* Left arrow */}
          <button
            onClick={() => setCurrentTipIndex((prev) => (prev - 1 + tips.length) % tips.length)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#9ca3af',
              fontSize: '16px',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.color = '#059669';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = '#9ca3af';
            }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          {/* Tip content */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flex: 1,
            justifyContent: 'center',
            minHeight: '48px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: '#ecfdf5',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className={`fas ${tips[currentTipIndex].icon}`} style={{ color: '#059669', fontSize: '16px' }}></i>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '2px'
              }}>
                {tips[currentTipIndex].title}
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                lineHeight: '1.4'
              }}>
                {tips[currentTipIndex].description}
              </div>
            </div>
          </div>

          {/* Right arrow */}
          <button
            onClick={() => setCurrentTipIndex((prev) => (prev + 1) % tips.length)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#9ca3af',
              fontSize: '16px',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.color = '#059669';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = '#9ca3af';
            }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {/* Dot indicators */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          marginTop: '12px'
        }}>
          {tips.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentTipIndex(index)}
              style={{
                width: index === currentTipIndex ? '20px' : '6px',
                height: '6px',
                borderRadius: '3px',
                background: index === currentTipIndex ? '#059669' : '#d1d5db',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>
      </div>

      {/* Floating Help Button — hidden in demo mode (SES unavailable) */}
      {!env.IS_DEMO && (
        <button
          onClick={() => setShowReportIssue(true)}
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '24px',
            width: '56px',
            height: '56px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 12px 32px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.3)';
          }}
          title="Report a problem"
        >
          <i className="fas fa-question" style={{ fontSize: '20px' }}></i>
        </button>
      )}

      {/* Report Issue Modal */}
      {!env.IS_DEMO && showReportIssue && (
        <ReportIssue onClose={() => setShowReportIssue(false)} />
      )}
    </div>
  );
};

export default HomePage;
